import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ListingGrid } from '../components/listings/ListingGrid';
import { listingsAPI, categoriesAPI } from '../lib/api';
import { 
  Search, 
  Truck, 
  Container, 
  Car, 
  Bike, 
  Ship, 
  Wrench, 
  Cog, 
  HardHat,
  ArrowRight,
  ChevronRight,
  Star,
  Users,
  Shield,
  TrendingUp
} from 'lucide-react';

const categoryIcons = {
  trucks: Truck,
  trailers: Container,
  camionetas: Car,
  cars: Car,
  motorcycles: Bike,
  boats: Ship,
  parts: Wrench,
  engines: Cog,
  equipment: HardHat,
};

const categoryImages = {
  trucks: 'https://images.unsplash.com/photo-1714903726946-c307906c3b35?w=400&h=300&fit=crop',
  trailers: 'https://images.unsplash.com/photo-1591768793355-74d04bb6608f?w=400&h=300&fit=crop',
  equipment: 'https://images.unsplash.com/photo-1763624578810-5ac518e4fd4c?w=400&h=300&fit=crop',
  parts: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop',
};

export default function HomePage() {
  const [featuredListings, setFeaturedListings] = useState([]);
  const [recentListings, setRecentListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, recentRes, categoriesRes] = await Promise.all([
          listingsAPI.getFeatured(4),
          listingsAPI.getRecent(8),
          categoriesAPI.getAll(),
        ]);
        setFeaturedListings(featuredRes.data);
        setRecentListings(recentRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/browse?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-primary overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left: text */}
            <div className="text-center lg:text-left order-2 lg:order-1">
              <span className="inline-block text-accent font-heading text-sm font-bold uppercase tracking-widest mb-4">
                El marketplace de Vehículos a tu servicio
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tighter text-white mb-6">
                Encuentra tu próximo<br />
                <span className="text-accent">Vehículo</span>
              </h1>
              <p className="text-base md:text-lg text-white/85 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                El marketplace líder de vehículos, camiones, remolques, maquinaria industrial, motocicletas y más. 
                Compra a vendedores y concesionarios verificados en todo el país.
              </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-2xl">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Busca por marca, modelo o palabra clave..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 pl-12 pr-4 text-lg bg-white border-0 rounded-sm"
                  data-testid="hero-search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="btn-accent h-14 px-8 text-base"
                data-testid="hero-search-btn"
              >
                Buscar
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-8 mt-10">
              <div className="text-white">
                <div className="font-heading text-3xl font-bold">10,000+</div>
                <div className="text-white/75 text-sm uppercase tracking-wide">Anuncios activos</div>
              </div>
              <div className="text-white">
                <div className="font-heading text-3xl font-bold">5,000+</div>
                <div className="text-white/75 text-sm uppercase tracking-wide">Vendedores verificados</div>
              </div>
              <div className="text-white">
                <div className="font-heading text-3xl font-bold">33</div>
                <div className="text-white/75 text-sm uppercase tracking-wide">Departamentos cubiertos</div>
              </div>
            </div>
            </div>

            {/* Right: collage image, fully visible & centered */}
            <div className="order-1 lg:order-2 flex justify-center">
              <img
                src="/images/landing-collage-es.png"
                alt="Vehimax — Todos los vehículos. Una plataforma."
                className="w-full max-w-xl rounded-md shadow-2xl ring-1 ring-white/10"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-accent font-heading text-sm font-bold uppercase tracking-widest">Explorar por</span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight mt-2">Categorías</h2>
            </div>
            <Link to="/browse" className="hidden md:flex items-center gap-2 text-sm font-semibold uppercase tracking-wide hover:text-accent transition-colors">Ver todos<ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {categories.slice(0, 5).map((category, index) => {
              const Icon = categoryIcons[category.id] || Truck;
              return (
                <Link
                  key={category.id}
                  to={`/browse?category=${category.id}`}
                  className="category-card bg-white border border-border p-6 hover:border-accent transition-colors duration-300 group animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`category-${category.id}`}
                >
                  <div className="bg-primary group-hover:bg-accent transition-colors duration-300 w-12 h-12 flex items-center justify-center rounded-sm mb-4">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="font-heading text-lg font-bold uppercase tracking-tight">
                    {category.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {category.subcategories?.length || 0} subcategories
                  </p>
                </Link>
              );
            })}
          </div>

          {/* More Categories Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {categories.slice(5, 9).map((category, index) => {
              const Icon = categoryIcons[category.id] || Truck;
              return (
                <Link
                  key={category.id}
                  to={`/browse?category=${category.id}`}
                  className="flex items-center gap-3 bg-white border border-border p-4 hover:border-accent transition-colors duration-300 group"
                  data-testid={`category-${category.id}`}
                >
                  <div className="bg-primary group-hover:bg-accent transition-colors duration-300 w-10 h-10 flex items-center justify-center rounded-sm">
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <span className="font-heading text-sm font-bold uppercase tracking-tight">
                    {category.name}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Listings */}
      {featuredListings.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <span className="text-accent font-heading text-sm font-bold uppercase tracking-widest flex items-center gap-2">
                  <Star className="h-4 w-4 fill-accent" />Destacado</span>
                <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight mt-2">Anuncios premium</h2>
              </div>
              <Link to="/browse?featured=true" className="hidden md:flex items-center gap-2 text-sm font-semibold uppercase tracking-wide hover:text-accent transition-colors">Ver todos los destacados<ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            <ListingGrid 
              listings={featuredListings} 
              loading={loading}
              columns={4}
            />
          </div>
        </section>
      )}

      {/* Recent Listings */}
      <section className="py-16 md:py-24 bg-secondary">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-accent font-heading text-sm font-bold uppercase tracking-widest">Recién agregados</span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight mt-2">Anuncios recientes</h2>
            </div>
            <Link to="/browse?sort_by=newest" className="hidden md:flex items-center gap-2 text-sm font-semibold uppercase tracking-wide hover:text-accent transition-colors">Ver todos<ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          <ListingGrid 
            listings={recentListings} 
            loading={loading}
            columns={4}
          />

          <div className="text-center mt-10">
            <Link to="/browse" data-testid="browse-all-btn">
              <Button className="btn-industrial">Ver todos los anuncios<ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-accent font-heading text-sm font-bold uppercase tracking-widest">Por qué Vehimax</span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight mt-2">La confianza de miles</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-secondary rounded-sm">
              <div className="bg-primary w-16 h-16 flex items-center justify-center rounded-sm mx-auto mb-6">
                <Shield className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight mb-3">Vendedores verificados</h3>
              <p className="text-muted-foreground leading-relaxed">Todos los concesionarios y vendedores están verificados para garantizar una experiencia segura y confiable.</p>
            </div>

            <div className="text-center p-8 bg-secondary rounded-sm">
              <div className="bg-accent w-16 h-16 flex items-center justify-center rounded-sm mx-auto mb-6">
                <Users className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight mb-3">Red en todo el país</h3>
              <p className="text-muted-foreground leading-relaxed">Conecta con compradores y vendedores en todo el país. Encuentra el vehículo perfecto donde sea.</p>
            </div>

            <div className="text-center p-8 bg-secondary rounded-sm">
              <div className="bg-primary w-16 h-16 flex items-center justify-center rounded-sm mx-auto mb-6">
                <TrendingUp className="h-8 w-8 text-white" />
              </div>
              <h3 className="font-heading text-xl font-bold uppercase tracking-tight mb-3">Análisis de mercado</h3>
              <p className="text-muted-foreground leading-relaxed">Obtén datos del mercado y análisis de precios en tiempo real para tomar mejores decisiones de compra y venta.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-primary text-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-accent font-heading text-sm font-bold uppercase tracking-widest">Empieza a vender</span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold uppercase tracking-tight mt-2 mb-6">Publica tu vehículo hoy</h2>
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Únete a miles de vendedores en Vehimax. Crea tu anuncio en minutos 
                y llega a compradores en todo el país. Anuncios gratis para vendedores particulares.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register?role=seller" data-testid="become-seller-btn">
                  <Button className="btn-accent">Vender como particular<ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/register?role=dealer" data-testid="dealer-program-btn">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-primary h-12 px-8 uppercase font-bold tracking-wide rounded-sm">Programa de concesionarios</Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1770175960229-dd21e8cf950a?w=600&h=400&fit=crop"
                alt="Commercial equipment"
                className="w-full h-auto rounded-sm shadow-2xl"
              />
              <div className="absolute -bottom-4 -left-4 bg-accent text-white p-4 rounded-sm">
                <div className="font-heading text-2xl font-bold">Gratis</div>
                <div className="text-sm uppercase tracking-wide">Para vendedores particulares</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
